<template>
  <v-container class="my-4">
    <!-- Header -->
    <v-layout row wrap align-center class="mb-4">
      <v-flex>
        <h2 class="headline font-weight-bold">
          <v-icon left color="primary">history</v-icon>
          Historial de Turnos
        </h2>
      </v-flex>
      <v-spacer></v-spacer>
    </v-layout>

    <!-- Filters -->
    <v-card class="elevation-2 rounded-lg mb-4 pa-3">
      <v-layout row wrap align-center>
        <v-flex xs12 sm3 class="px-2">
          <v-autocomplete
            v-model="filters.guardId"
            :items="guardOptions"
            item-text="label"
            item-value="value"
            label="Guardia"
            clearable
            prepend-icon="person"
            hide-details
          ></v-autocomplete>
        </v-flex>
        <v-flex xs12 sm3 class="px-2">
          <v-text-field
            v-model="filters.startDate"
            label="Fecha inicio"
            type="date"
            prepend-icon="event"
            clearable
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-2">
          <v-text-field
            v-model="filters.endDate"
            label="Fecha fin"
            type="date"
            prepend-icon="event_available"
            clearable
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-2 text-xs-right">
          <v-btn color="primary" round @click="fetchHistory(true)" :loading="loading">
            <v-icon left>search</v-icon> Buscar
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Summary chips -->
    <v-layout row wrap class="mb-4" v-if="!loading && totalGuards > 0">
      <v-chip color="primary" text-color="white" class="mr-2">
        <v-icon left small>people</v-icon>
        {{ totalGuards }} guardias
      </v-chip>
      <v-chip color="info" text-color="white">
        <v-icon left small>access_time</v-icon>
        {{ totalShifts }} turnos
      </v-chip>
    </v-layout>

    <!-- Loading -->
    <div v-if="loading" class="text-xs-center py-8">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      <div class="mt-3 grey--text subheading">Cargando historial...</div>
    </div>

    <!-- Error -->
    <v-alert v-else-if="errorMsg" type="error" :value="true" class="mb-4">
      {{ errorMsg }}
    </v-alert>

    <!-- Empty -->
    <v-alert v-else-if="!loading && guards.length === 0" type="info" :value="true">
      No se encontraron turnos con los filtros seleccionados.
    </v-alert>

    <!-- Guards list -->
    <div v-else>
      <v-expansion-panel expand>
        <v-expansion-panel-content
          v-for="guard in guards"
          :key="guard.guard_id"
          class="mb-2 elevation-1 rounded-lg"
        >
          <template v-slot:header>
            <v-layout row wrap align-center>
              <v-avatar size="36" color="primary lighten-4" class="mr-3">
                <v-icon color="primary">person</v-icon>
              </v-avatar>
              <div style="flex:1;">
                <div class="font-weight-bold body-2">{{ guard.guard_name }}</div>
                <div class="caption grey--text">
                  @{{ guard.guard_username }}
                  <span v-if="guard.badge_number"> · Gafete: {{ guard.badge_number }}</span>
                  <span v-if="guard.assigned_company_name"> · {{ guard.assigned_company_name }}</span>
                </div>
              </div>
              <div class="text-xs-right mr-3">
                <v-chip
                  :color="guard.active_shifts > 0 ? 'success' : 'grey lighten-2'"
                  :text-color="guard.active_shifts > 0 ? 'white' : 'grey darken-1'"
                  small
                  class="mr-1"
                >
                  <v-icon left x-small>{{ guard.active_shifts > 0 ? 'radio_button_checked' : 'radio_button_unchecked' }}</v-icon>
                  {{ guard.active_shifts > 0 ? 'En turno' : 'Libre' }}
                </v-chip>
                <v-chip small color="blue-grey lighten-4" text-color="blue-grey darken-2">
                  {{ guard.total_shifts }} turno{{ guard.total_shifts !== 1 ? 's' : '' }}
                </v-chip>
              </div>
            </v-layout>
          </template>

          <!-- Shifts table for this guard -->
          <v-card flat>
            <v-data-table
              :headers="shiftHeaders"
              :items="guard.history"
              hide-actions
              class="elevation-0"
              :no-data-text="'Sin turnos registrados'"
            >
              <template v-slot:items="props">
                <td>
                  <v-chip
                    :color="props.item.is_active ? 'success' : 'blue-grey lighten-4'"
                    :text-color="props.item.is_active ? 'white' : 'blue-grey darken-2'"
                    small
                  >
                    {{ props.item.is_active ? 'Activo' : 'Finalizado' }}
                  </v-chip>
                </td>
                <td>{{ props.item.caseta_name || '-' }}</td>
                <td>{{ formatDateTime(props.item.start_time) }}</td>
                <td>{{ props.item.end_time ? formatDateTime(props.item.end_time) : '—' }}</td>
                <td class="grey--text">{{ duration(props.item.start_time, props.item.end_time) }}</td>
              </template>
            </v-data-table>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Pagination -->
      <v-layout row wrap align-center justify-center class="mt-4" v-if="pagination.count > pagination.limit">
        <v-btn
          flat
          :disabled="pagination.previousOffset === null"
          @click="paginate(pagination.previousOffset)"
          color="primary"
        >
          <v-icon left>chevron_left</v-icon> Anterior
        </v-btn>
        <span class="mx-3 grey--text caption">
          Guardias {{ pagination.offset + 1 }}–{{ Math.min(pagination.offset + pagination.limit, pagination.count) }}
          de {{ pagination.count }}
        </span>
        <v-btn
          flat
          :disabled="pagination.nextOffset === null"
          @click="paginate(pagination.nextOffset)"
          color="primary"
        >
          Siguiente <v-icon right>chevron_right</v-icon>
        </v-btn>
      </v-layout>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios';
import store from '@/store';
import moment from 'moment';
import 'moment/locale/es';

const API_BASE = 'https://empresa-service.intrare.app';

export default {
  name: 'SecurityShiftHistory',
  data() {
    return {
      loading: false,
      errorMsg: '',
      companyName: '',
      totalGuards: 0,
      totalShifts: 0,
      guards: [],
      guardOptions: [],
      pagination: {
        limit: 20,
        offset: 0,
        count: 0,
        nextOffset: null,
        previousOffset: null
      },
      filters: {
        guardId: null,
        startDate: '',
        endDate: ''
      },
      shiftHeaders: [
        { text: 'Estado',    value: 'is_active',  sortable: false },
        { text: 'Caseta',    value: 'caseta_name', sortable: false },
        { text: 'Inicio',    value: 'start_time',  sortable: false },
        { text: 'Fin',       value: 'end_time',    sortable: false },
        { text: 'Duración',  value: 'duration',    sortable: false }
      ]
    };
  },
  computed: {
    headers() {
      return {
        'Content-Type': 'application/json',
        'authorization': 'Token ' + store.getters.getToken
      };
    }
  },
  created() {
    this.fetchHistory(true);
    this.fetchGuardOptions();
  },
  methods: {
    async fetchGuardOptions() {
      try {
        const res = await axios.get(`${API_BASE}/security/guards/`, { headers: this.headers });
        const guards = Array.isArray(res.data) ? res.data : (res.data.results || []);
        this.guardOptions = guards.map(g => ({
          value: g.guard_id || g.id,
          label: `${g.first_name} ${g.last_name} (@${g.username})`
        }));
      } catch (_) { /* non-critical */ }
    },
    async fetchHistory(resetOffset = false) {
      if (resetOffset) this.pagination.offset = 0;
      this.loading = true;
      this.errorMsg = '';

      const params = {
        limit: this.pagination.limit,
        offset: this.pagination.offset
      };
      if (this.filters.guardId)  params.guard_id   = this.filters.guardId;
      if (this.filters.startDate) params.start_date = this.filters.startDate;
      if (this.filters.endDate)   params.end_date   = this.filters.endDate;

      try {
        const res = await axios.get(`${API_BASE}/security/shifts/all-history/`, {
          headers: this.headers,
          params
        });
        const data = res.data;
        this.companyName    = data.security_company_name || '';
        this.totalGuards    = data.total_guards || 0;
        this.totalShifts    = data.total_shifts || 0;
        this.guards         = data.guards || [];
        this.pagination.count          = data.pagination.count;
        this.pagination.nextOffset     = data.pagination.next_offset;
        this.pagination.previousOffset = data.pagination.previous_offset;
      } catch (err) {
        const msg = err.response && err.response.data
          ? (err.response.data.error || err.response.data.detail || 'Error al cargar el historial')
          : 'Error al cargar el historial';
        this.errorMsg = msg;
        this.guards = [];
      } finally {
        this.loading = false;
      }
    },
    paginate(offset) {
      this.pagination.offset = offset;
      this.fetchHistory(false);
    },
    formatDateTime(dt) {
      if (!dt) return '—';
      return moment(dt).locale('es').format('DD/MM/YYYY HH:mm');
    },
    duration(start, end) {
      if (!start) return '—';
      const from = moment(start);
      const to = end ? moment(end) : moment();
      const mins = to.diff(from, 'minutes');
      if (mins < 60) return `${mins} min`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
  }
};
</script>
